import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ITimezone } from "react-timezone-select";
import { productQueries } from "@/api/product";
import { Job, UpdateJob } from "@/api/scheduledJobs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import YouTubeInput from "@/components/YouTubeInput";
import { TimeAndTzPicker } from "@/components/TimeAndTzPicker";
import { TweetSelector } from "./TweetSelector";

// -------------- Props --------------
interface EditJobDialogProps {
  onClose: () => void;
  onSave: (job: UpdateJob) => Promise<void>;
  jobData: Job;
}

// -------------- Main --------------
export function EditJobDialog({
  onClose,
  onSave,
  jobData,
}: EditJobDialogProps) {
  const { toast } = useToast();

  // 1) Load all products
  const {
    data: products = [],
    isLoading: isLoadingProducts,
    error: productsError,
  } = useQuery(productQueries.products());

  // 2) Find matching product
  const product = useMemo(() => {
    return products.find((p) => p.product_sku === jobData.product_sku) || null;
  }, [products, jobData.product_sku]);

  // 3) Local time fields
  const [hour, setHour] = useState<number>(12);
  const [minute, setMinute] = useState<number>(0);

  const [recurringOrOneOff, setRecurringOrOneOff] = useState<
    "oneoff" | "recurring"
  >(jobData.recurring_or_one_off === "recurring" ? "recurring" : "oneoff");
  const [recurringFrequency, setRecurringFrequency] = useState<
    "" | "daily" | "weekly"
  >(jobData.recurring_frequency || "");

  // 4) Local metadata
  const [kolsCount, setKolsCount] = useState<number>(
    typeof jobData.metadata?.kols_count === "number"
      ? jobData.metadata.kols_count
      : 1,
  );
  const [topic, setTopic] = useState<string>(jobData.metadata?.topic || "");

  const [tone, setTone] = useState<string>(jobData.metadata?.tone || "");

  const [youtubeUrl, setYoutubeUrl] = useState<string>(
    jobData.metadata?.youtube_url || "",
  );

  // Add state for selected tweet ID
  const [selectedTweetId, setSelectedTweetId] = useState<string | null>(
    jobData.metadata?.tweet_id || null,
  );

  // 5) Timezone
  // If the job has a timezone, use that; else default to the user's local or "UTC"
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  // we store either a string or an object
  // if jobData.timezone is a recognized tz string, let's do an object { value, label }
  // to ensure TimezoneSelect picks it up right away
  const initialTz: ITimezone | string = jobData.timezone
    ? { value: jobData.timezone, label: jobData.timezone }
    : localTz;

  const [timeZone, setTimeZone] = useState<ITimezone | string>(initialTz);

  // 6) Parse jobData.execution_time_in_tz => hour/min
  useEffect(() => {
    if (jobData.execution_time_in_tz) {
      const [hStr, mStr] = jobData.execution_time_in_tz.split(":");
      const h = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      if (!isNaN(h) && h >= 0 && h <= 23) setHour(h);
      if (!isNaN(m) && m >= 0 && m <= 59) setMinute(m);
    }
  }, [jobData.execution_time_in_tz]);

  // 7) Logic: threads => must be one-off
  const productAllowsRecurring = useMemo(() => {
    if (!product) return false;
    const sku = product.product_sku;
    return !(
      (
        sku === "twitter-threads-custom-topic" ||
        sku === "twitter-threads-youtube-threader" ||
        sku === "twitter-posting-post-existing-tweet" ||
        sku === "instagram-posting-post-existing-content"
      ) // Add this to disallow recurring for tweet posting
    );
  }, [product]);

  const productNeedsKolsSlider =
    product?.product_sku === "twitter-engagement-engage-with-kols";

  const productTopicIsRequired =
    product?.product_sku === "twitter-threads-custom-topic";

  const productTopicIsOptional = useMemo(() => {
    if (!product) return false;
    const optionalSkus = [
      "twitter-playground-create-news-post",
      "twitter-posting-create-image-post",
      "twitter-playground-create-worldview-post",
      "twitter-posting-create-news-post",
      "twitter-posting-create-worldview-post",
    ];
    return optionalSkus.includes(product.product_sku);
  }, [product]);

  const productToneIsOptional = useMemo(() => {
    if (!product) return false;
    const optionalSkus = [
      "twitter-playground-create-news-post",
      "twitter-playground-create-worldview-post",
      "twitter-posting-create-news-post",
      "twitter-posting-create-worldview-post",
      "twitter-engagement-engage-with-kols",
    ];
    return optionalSkus.includes(product.product_sku);
  }, [product]);

  const productNeedsYoutubeUrl =
    product?.product_sku === "twitter-threads-youtube-threader";

  // Add check for tweet selection
  const productNeedsTweetSelection = useMemo(() => {
    return product?.product_sku === "twitter-posting-post-existing-tweet";
  }, [product]);

  const [isSaving, setIsSaving] = useState(false);

  // 8) Handler: final save
  async function handleUpdateJob() {
    try {
      if (!product) {
        toast({ description: "Product not found. Can't edit." });
        return;
      }

      // if product doesn't allow recurring => force oneoff
      if (!productAllowsRecurring) {
        setRecurringOrOneOff("oneoff");
        setRecurringFrequency("");
      }

      // if recurring but no freq => error
      if (recurringOrOneOff === "recurring" && !recurringFrequency) {
        toast({ description: "Please select daily or weekly for recurring." });
        return;
      }

      // if topic required but empty => error
      if (productTopicIsRequired && !topic) {
        toast({ description: "Topic is required for this product." });
        return;
      }

      // if youtube needed but empty => error
      if (productNeedsYoutubeUrl && !youtubeUrl) {
        toast({ description: "YouTube URL is required." });
        return;
      }

      // if tweet selection is needed but empty => error
      if (productNeedsTweetSelection && !selectedTweetId) {
        toast({ description: "Please select a tweet to post." });
        return;
      }

      // zero-pad hour/min
      const hh = hour.toString().padStart(2, "0");
      const mm = minute.toString().padStart(2, "0");

      // convert timeZone to final string
      let finalTz = "UTC";
      if (typeof timeZone === "string") {
        finalTz = timeZone;
      } else if (timeZone && typeof timeZone.value === "string") {
        finalTz = timeZone.value;
      }

      // build updated job
      const updateObj = {
        job_id: jobData.job_id,
        agent_id: jobData.agent_id,
        product_sku: jobData.product_sku,
        job_name: jobData.job_name || product.name,
        recurring_or_one_off: recurringOrOneOff,
        recurring_frequency:
          recurringOrOneOff === "recurring" ? recurringFrequency : "",
        generated_by: jobData.generated_by || "human",
        status: jobData.status || "pending",
        timezone: finalTz,
        execution_time_in_tz: `${hh}:${mm}`,
        metadata: {
          ...jobData.metadata,
        },
      } satisfies UpdateJob;

      // kols slider
      if (productNeedsKolsSlider) {
        updateObj.metadata.kols_count = kolsCount;
      } else {
        delete updateObj.metadata.kols_count;
      }
      // youtube
      if (productNeedsYoutubeUrl) {
        updateObj.metadata.youtube_url = youtubeUrl;
      } else {
        delete updateObj.metadata.youtube_url;
      }
      // topic
      if (productTopicIsRequired || productTopicIsOptional) {
        updateObj.metadata.topic = topic;
      } else {
        delete updateObj.metadata.topic;
      }

      if (productToneIsOptional) {
        updateObj.metadata.tone = tone;
      } else {
        delete updateObj.metadata.tone;
      }

      // Add tweet_id to metadata if needed
      if (productNeedsTweetSelection) {
        updateObj.metadata.tweet_id = selectedTweetId;
      } else {
        delete updateObj.metadata.tweet_id;
      }

      setIsSaving(true);
      await onSave(updateObj);
      onClose();
    } catch (err) {
      console.error("Error updating job:", err);
      toast({ description: "Failed to update job." });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[50dvw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Scheduled Job</DialogTitle>
          <DialogDescription>
            Modify the existing job's parameters below.
          </DialogDescription>
        </DialogHeader>

        {isLoadingProducts && (
          <div className="flex justify-center items-center min-h-[200px]">
            Loading products...
          </div>
        )}
        {productsError && (
          <div className="text-red-500">
            Error loading products: {String(productsError)}
          </div>
        )}

        {!isLoadingProducts && !productsError && (
          <div className="space-y-6">
            {product ? (
              <Card className="p-4 space-y-4 bg-[color:rgba(255,255,255,0.05)]">
                <div className="text-md font-semibold">
                  {product.name}{" "}
                  <Badge variant="outline">{product.product_sku}</Badge>
                </div>
                <div className="text-sm text-muted-foreground mb-2">
                  {product.description}
                </div>

                {/* One-off vs. Recurring */}
                {productAllowsRecurring ? (
                  <div className="flex flex-wrap items-center gap-4">
                    <Label>Schedule Type:</Label>
                    <Button
                      variant={
                        recurringOrOneOff === "oneoff" ? "default" : "outline"
                      }
                      onClick={() => {
                        setRecurringOrOneOff("oneoff");
                        setRecurringFrequency("");
                      }}
                    >
                      One-off
                    </Button>
                    <Button
                      variant={
                        recurringOrOneOff === "recurring"
                          ? "default"
                          : "outline"
                      }
                      onClick={() => setRecurringOrOneOff("recurring")}
                    >
                      Recurring
                    </Button>
                    {recurringOrOneOff === "recurring" && (
                      <div className="flex items-center gap-2">
                        <Label>Every:</Label>
                        <Button
                          variant={
                            recurringFrequency === "daily"
                              ? "default"
                              : "outline"
                          }
                          onClick={() => setRecurringFrequency("daily")}
                        >
                          Daily
                        </Button>
                        <Button
                          variant={
                            recurringFrequency === "weekly"
                              ? "default"
                              : "outline"
                          }
                          onClick={() => setRecurringFrequency("weekly")}
                        >
                          Weekly
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <Label>Schedule:</Label>
                    <p className="text-sm text-muted-foreground">
                      This product is one-off only.
                    </p>
                  </div>
                )}

                {/* Time + Timezone */}
                <TimeAndTzPicker
                  hour={hour}
                  setHour={setHour}
                  minute={minute}
                  setMinute={setMinute}
                  timeZone={timeZone}
                  setTimeZone={setTimeZone}
                />

                {/* Additional fields */}
                {productNeedsKolsSlider && (
                  <div>
                    <Label>KOLs to engage: {kolsCount}</Label>
                    <Slider
                      min={1}
                      max={5}
                      step={1}
                      value={[kolsCount]}
                      onValueChange={([val]) => setKolsCount(val)}
                      className="max-w-sm mt-2"
                    />
                  </div>
                )}

                {productNeedsYoutubeUrl && (
                  <div>
                    <Label>YouTube URL</Label>
                    <YouTubeInput
                      value={youtubeUrl}
                      onChange={(val) => setYoutubeUrl(val)}
                      isLoading={isSaving}
                    />
                  </div>
                )}

                {(productTopicIsRequired || productTopicIsOptional) && (
                  <div>
                    <Label>
                      Topic{productTopicIsRequired ? " (Required)" : ""}
                    </Label>
                    <Textarea
                      className="mt-1"
                      placeholder={
                        productTopicIsRequired
                          ? "Enter topic for custom threads..."
                          : "Enter topic or leave empty if none"
                      }
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                    />
                  </div>
                )}

                {productToneIsOptional && (
                  <div>
                    <Label>
                      Tone{productToneIsOptional ? " (Optional)" : ""}
                    </Label>
                    <Textarea
                      className="mt-1"
                      placeholder={"Enter tone of message..."}
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                    />
                  </div>
                )}

                {/* Add Tweet Selection UI */}
                {productNeedsTweetSelection && (
                  <div>
                    <Label>Select Tweet to Post (Required)</Label>
                    <TweetSelector
                      personaId={jobData.agent_id}
                      onSelectTweet={setSelectedTweetId}
                      selectedTweetId={selectedTweetId}
                    />
                    {selectedTweetId && (
                      <div className="mt-2 text-sm text-green-500">
                        Tweet selected: {selectedTweetId}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            ) : (
              <div className="text-red-500">
                Could not find product for SKU: {jobData.product_sku}
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleUpdateJob} disabled={!product || isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
